// --------------------------------------------------------------
// Created On: 2025-04-02
// Author: Hannah Vaughan
//
// Last Modified: 2025-12-18
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useState } from "react";
import { COPILOT_DEVICE_TYPE, COPILOT_EDGE_DEVICE_TYPE, COPILOT_PMG_DEVICE_TYPE } from "src/constants/miscellaneous";
import { BREAKPOINT_SMALL } from "src/constants/breakpoints";
import ConfirmModal from "src/components/ConfirmModal/ConfirmModal";
import styles from "./MonitoringDeviceOperationControls.module.scss";

// Container for all monitoring device operation controls based on device type for the Monitoring Device Operations
// section in the Remote Operation Panel.
export default function MonitoringDeviceOperationControls(props: Props): Component {
  const [showClearModal, setShowClearModal] = useState<boolean>(false);
  const [showRestartModal, setShowRestartModal] = useState<boolean>(false);
  const [showReapplyModal, setShowReapplyModal] = useState<boolean>(false);

  return (
    <div className="container">
      <label className={styles.controlLabel}>Monitoring Device Operations</label>

      {[COPILOT_DEVICE_TYPE, COPILOT_PMG_DEVICE_TYPE].includes(props.deviceType) && (
        <Fragment>
          <div className="row align-items-center">
            {props.isDeviceAttached && (
              <div className={`col px-3 ${styles.controlColumn}`}>
                <button
                  className="btn btn-primary w-100"
                  type="button"
                  disabled={props.disabled}
                  onClick={() => setShowClearModal(true)}
                >
                  {props.windowDimensionWidth > BREAKPOINT_SMALL ? "Clear Device" : "Clear"}
                </button>
              </div>
            )}

            {props.isDeviceAttached && (
              <div className={`col px-3 ${styles.controlColumn}`}>
                <button
                  className="btn btn-primary w-100"
                  type="button"
                  disabled={props.disabled}
                  onClick={() => setShowRestartModal(true)}
                >
                  {props.windowDimensionWidth > BREAKPOINT_SMALL ? "Restart Device" : "Restart"}
                </button>
              </div>
            )}
          </div>

          <div className="row align-items-center">
            <div className={`col px-3 ${styles.controlColumn}`}>
              <button
                className="btn btn-primary w-100"
                type="button"
                disabled={props.disabled}
                onClick={() => setShowReapplyModal(true)}
              >
                {props.windowDimensionWidth > BREAKPOINT_SMALL ? "Reapply Configuration" : "Configure"}
              </button>
            </div>
          </div>
        </Fragment>
      )}

      {props.deviceType === COPILOT_EDGE_DEVICE_TYPE && (
        <div className="row align-items-center">
          <div className={`col px-3 ${styles.controlColumn}`}>
            <button
              className="btn btn-primary w-100"
              type="button"
              disabled={props.disabled}
              onClick={() => props.onBlinkLeds()}
            >
              Blink LEDs
            </button>
          </div>
          <div className={`col px-3 ${styles.controlColumn}`}>
            <button
              className="btn btn-primary w-100"
              type="button"
              disabled={props.disabled}
              onClick={() => setShowReapplyModal(true)}
            >
              {props.windowDimensionWidth > BREAKPOINT_SMALL ? "Reapply Configuration" : "Configure"}
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        showModal={showClearModal}
        title={`Clear failed commands?`}
        content={
          "Clearing failed commands on the monitoring device may resolve issues where new commands are failing to be received. " +
          "Do you want to clear failed commands?"
        }
        yesText="Clear Failed Commands"
        noText="Cancel"
        danger={true}
        onClose={() => setShowClearModal(false)}
        onYes={() => {
          props.onClearFailedCommands();
          setShowClearModal(false);
        }}
        onNo={() => setShowClearModal(false)}
      />

      <ConfirmModal
        showModal={showRestartModal}
        title={`Restart monitoring device?`}
        content={
          "Restarting the monitoring device may resolve issues where new commands are failing to be received. " +
          "The monitoring device will not be accessible and will not report data while it is restarting. " +
          "Do you want to restart the monitoring device?"
        }
        yesText="Restart Monitoring Device"
        noText="Cancel"
        danger={true}
        onClose={() => setShowRestartModal(false)}
        onYes={() => {
          props.onRestartMonitoringDevice();
          setShowRestartModal(false);
        }}
        onNo={() => setShowRestartModal(false)}
      />

      <ConfirmModal
        showModal={showReapplyModal}
        title="Reapply Configuration?"
        content={
          "Reapplying a configuration will overwrite the current configuration on the device to match the current asset details. " +
          "You will get a notification when the process is complete. " +
          "Would you like to reapply the configuration?"
        }
        yesText="Reapply Configuration"
        noText="Cancel"
        danger={true}
        onClose={() => setShowReapplyModal(false)}
        onYes={() => {
          props.onReapplyConfiguration();
          setShowReapplyModal(false);
        }}
        onNo={() => setShowReapplyModal(false)}
      />
    </div>
  );
}

interface Props {
  deviceType: string;
  deviceId: number | null;
  isDeviceAttached: boolean;
  disabled: boolean;
  firmwareUpdateInitialized: boolean;
  onLatestFirmware: boolean | null;
  windowDimensionWidth: number;
  onClearFailedCommands: () => void;
  onRestartMonitoringDevice: () => void;
  onReapplyConfiguration: () => void;
  onBlinkLeds: () => void;
  onTestConfiguration: () => void;
}
